import type { Max, MaxRead, MaxSource } from "./schemas/max.js";
import type { MaxHistoryItem } from "./maxes.js";
import { inTimeRange, type TimeRange } from "./timeRange.js";

// PG-59: "PR timeline with provenance". Flattens `fetchMaxesHistory`'s
// per-exercise `{current, history}` items into one feed of MAX rows, newest
// first, so the widget shows what changed and WHERE it came from
// (max.source), without re-deriving trust (isMaxTrustedForProposal already
// owns that partition in schemas/max.ts).

/** Hard cap on rows returned: a timeline widget, not a full export. */
export const MAX_TIMELINE_ROW_CAP = 12;

export type MaxTimelineRow = {
  exerciseId: string;
  valueKg: number;
  setAt: string;
  source: MaxSource;
  /** source === "tested": a confirmed single-rep PR, distinct from every
   *  other source (manual guess, derived projection, seed, import). */
  tested: boolean;
};

export type MaxTimeline = {
  /** Newest-first, capped at MAX_TIMELINE_ROW_CAP. */
  rows: MaxTimelineRow[];
  /** Rows before the cap: `rows.length < total` is the truncation signal
   *  the widget needs to say "last N of total" instead of cutting silently. */
  total: number;
};

// A MAX without a date (a coach reading one from before the bond, #333) has no
// place on a timeline: no row, rather than a made-up date.
const hasDate = (m: MaxRead): m is Max => m.setAt !== undefined;

const toRow = (m: Max): MaxTimelineRow => ({
  exerciseId: m.exerciseId,
  valueKg: m.valueKg,
  setAt: m.setAt,
  source: m.source,
  tested: m.source === "tested",
});

/**
 * Newest-first MAX timeline across every exercise. An exercise with no
 * history yet (fresh MAX, never revised) falls back to its `current` row
 * alone, so it still appears once. Dedupes by exerciseId+setAt, the same
 * defence `fetchMaxesDetailed`'s callers rely on against a row appearing
 * twice across `current` and `history`. With a `range` (PG-64, the report's
 * «PRs in the period») only the rows set inside it count, the total too.
 */
export function buildMaxTimeline(
  items: ReadonlyArray<MaxHistoryItem>,
  range?: TimeRange,
): MaxTimeline {
  const seen = new Set<string>();
  const rows: MaxTimelineRow[] = [];
  for (const { current, history } of items) {
    const entries = (history.length > 0 ? history : [current]).filter(hasDate);
    for (const entry of entries) {
      const key = `${entry.exerciseId}#${entry.setAt}`;
      if (seen.has(key)) continue;
      if (range && !inTimeRange(Date.parse(entry.setAt), range)) continue;
      seen.add(key);
      rows.push(toRow(entry));
    }
  }
  rows.sort((a, b) => b.setAt.localeCompare(a.setAt));
  return { rows: rows.slice(0, MAX_TIMELINE_ROW_CAP), total: rows.length };
}
